// University of Vermont: public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UVM's Common Data Set 2025-2026, linked from the Office of
//    Institutional Research and Analytics' Common Data Set page (a print of
//    the CDS Excel template; some page footers still read "Common Data Set
//    2024-2025", but the cover, C1 and G1 are the 2025-2026 edition): C1
//    applicants, admits and enrollees for Fall 2025 with the residency table,
//    C8 test policy for Fall 2027, C21 early decision counts, C22 early
//    action, G1/G5 2026-2027 costs.
//  - UVM Undergraduate Admissions: Application Deadlines (dates for the
//    application that opens August 1, 2026, Fall 2027 entry) and How to
//    Apply: First Years (test-optional, no application fee).
//  - UVM Student Financial Services, Regional Tuition Break (new for the fall
//    2027 entering class).
//  - College Scorecard API, school id 231174 (net price by family income).
// Departure: the CDS C13 lists a $55 application fee (for the Fall 2026
// cycle); the admissions page and the 2026-27 catalogue say there is no cost
// to apply, and the admissions page wins. Cost: the CDS G1/G5 items; the
// CDS gives no total, so the totals are the sum of the items.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Vermont Common Data Set 2025-2026',
        url: 'https://www.uvm.edu/oira/common-data-set',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UVM Admissions, Application Deadlines',
        url: 'https://www.uvm.edu/admissions/undergraduate/application-deadlines',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UVM Admissions, How to Apply: First Years',
        url: 'https://www.uvm.edu/admissions/undergraduate/how-apply-first-years',
        read: '2026-10-01',
    },
    tuitionBreak: {
        label: 'UVM Student Financial Services, Regional Tuition Break',
        url: 'https://www.uvm.edu/studentfinancialservices/regional-tuition-break',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?231174-University-of-Vermont',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-vermont',
    kbId: 'university_of_vermont',
    name: 'University of Vermont',
    shortName: 'UVM',
    aliases: ['UVM'],
    location: 'Burlington, VT',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UVM color palette); decoration only.
    branding: {
        primary: '#154734',
        secondary: '#FFD100',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_vermont_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 25434,
        admitted: 18576,
        enrolled: 2810,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'Vermont residents', applied: 1948, admitted: 1491 },
            { group: 'Out-of-state', applied: 22205, admitted: 16695 },
            { group: 'International', applied: 1281, admitted: 390 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UVM\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early decision', applied: 396, admitted: 363 },
        ],
        note: 'The Common Data Set gives one early decision count for the fall 2025 class and no early action counts, so there is no early action rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Late November', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Typically in late December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'February 4, 2027', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Typically in late February', binding: false },
        ],
        note: 'The application opens August 1, 2026, on the Common App or the Coalition App with Scoir, and there is no cost to apply. The date shown is for the application itself; the rest of your materials are due November 13 for Early Action and Early Decision I, and January 29 for Early Decision II and Regular Decision. Applications that arrive after a deadline are considered as space allows. The acceptance fee is due December 11, 2026 for Early Decision I, February 22, 2027 for Early Decision II, and May 1, 2027 for Early Action and Regular Decision.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding: if admitted, you withdraw your other applications and enroll. You, a parent or guardian, and your college counselor sign the Early Decision Agreement. You are not bound if you are admitted to a major that was not your first choice, or if your Early Decision I application is deferred to the regular round, which brings a non-binding decision in late February.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UVM is test-optional. If you want your ACT or SAT scores read, you report them yourself on the application, and send official scores only if you are admitted and enroll. To change your testing choice, you must do so by the application completion deadline. If you want to send November SAT scores, apply Regular Decision.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, for fall 2027 applicants, with scores due by February 3.',
        cdsWhere: 'section C8',
    },

    currentChanges: [
        { topic: 'Regional Tuition Break', line: 'Starting with the fall 2027 entering class, first-year and transfer students from Connecticut, Maine, Massachusetts, New Hampshire and Rhode Island who enroll in an eligible major in the College of Agriculture and Life Sciences, the College of Education and Social Services or the Rubenstein School of Environment and Natural Resources pay 175% of the in-state tuition rate, estimated at $30,240 for 2027-2028. They must stay in an eligible major to keep the rate.', source: 'tuitionBreak' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [16938, 46655] },
            { item: 'Required fees', amounts: [3058, 3058] },
            { item: 'Housing (on campus)', amounts: [9614, 9614] },
            { item: 'Food (on-campus meal plan)', amounts: [5040, 5040] },
            { item: 'Books and supplies', amounts: [1320, 1320] },
            { item: 'Transportation', amounts: [630, 630] },
            { item: 'Other expenses', amounts: [2443, 2443] },
        ],
        totals: [39043, 68760],
        note: 'For a full-time undergraduate living on campus, fall and spring. UVM reports that tuition does not vary by year or by program.',
        source: 'cds',
        where: 'sections G1, G3, G4 and G5',
        netPrice: {
            average: 20277,
            byIncome: [
                { income: '$0 to $30,000', amount: 11915 },
                { income: '$30,001 to $48,000', amount: 12762 },
                { income: '$48,001 to $75,000', amount: 14146 },
                { income: '$75,001 to $110,000', amount: 19946 },
                { income: 'Over $110,000', amount: 28966 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UVM\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
